import { useState } from 'react';
import { PortfolioTypeBadge } from '../shared/PortfolioTypeBadge';

/**
 * Onboarding Step 3: Name Your Portfolio.
 * Simple portfolio name input + starting cash display.
 */
export function OnboardingStep3({ config, portfolioId, portfolioName, jwtToken, onComplete, onBack }) {
  const [name, setName] = useState(portfolioName || '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;

    setSubmitting(true);
    setError(null);
    try {
      // Only update if name changed
      if (trimmed !== portfolioName) {
        const res = await fetch(`${config.apiBaseUrl}/portfolio/${portfolioId}`, {
          method: 'PUT',
          headers: { Authorization: `Bearer ${jwtToken}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: trimmed }),
        });
        if (!res.ok) throw new Error(`Update portfolio failed (${res.status})`);
      }
      onComplete({ portfolioName: trimmed });
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col gap-4 mt-2">
      <div>
        <div className="text-sm font-semibold text-white mb-1">Name your portfolio</div>
        <div className="text-xs text-slate-400">Give your paper trading workspace a name you'll recognize.</div>
      </div>

      <div>
        <label className="block text-sm text-slate-400 mb-1">Portfolio Name</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-cyan-500"
          placeholder="e.g. My First Strategy"
        />
      </div>

      <div className="bg-slate-800 border border-slate-700 rounded-lg p-4 flex items-center justify-between">
        <div>
          <div className="text-xs text-slate-400 mb-1">Starting Cash</div>
          <div className="text-lg font-bold text-white">$10,000</div>
        </div>
        <PortfolioTypeBadge portfolioType="HL_PAPER" isTestnet={true} size="sm" />
      </div>

      {error && <p className="text-red-400 text-xs">{error}</p>}

      <hr className="border-slate-700 border-dashed" />

      <div className="flex items-center justify-between">
        <button onClick={onBack} className="text-xs text-slate-400 hover:text-slate-200">← back</button>
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-500">step 3/4</span>
          <button
            onClick={handleSubmit}
            disabled={submitting || !name.trim()}
            className={`px-4 py-2 rounded text-sm font-medium text-white ${
              submitting || !name.trim() ? 'bg-cyan-900 opacity-50' : 'bg-cyan-600 hover:bg-cyan-500'
            }`}
          >
            {submitting ? 'Saving…' : 'continue →'}
          </button>
        </div>
      </div>
    </div>
  );
}
